"use client";

import React, { useCallback, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { UserCheck } from "lucide-react";
import { ResponsiveCommandSelect } from "@/components/ui/responsive-select";
import { ClientSelectWithModal } from "@/components/ui/selects";
import { CompanySelect } from "@/components/ui/selects/CompanySelect";
import { TicketSelectWithModal } from "@/components/ui/selects/components/TicketSelectWithModal";
import { UserSelect } from "@/components/shared/UserSelect";
import { MultiUserSelect } from "@/components/shared/selects/MultiUserSelect";
import { FieldWrapper } from "./context";
import { cn } from "@/lib/utils";
import type { FieldRenderContext } from "./context";

/**
 * Render a select field.
 */
export const SelectFieldRenderer: React.FC<{ ctx: FieldRenderContext }> = ({ ctx }) => {
  const { fieldName, field, setValue, watchedValues, errors } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string;
  const selectCurrentValue = watchedValues[fieldName];

  return (
    <FieldWrapper
      fieldName={fieldName}
      field={field}
      hasError={hasError}
      errorMessage={errorMessage || `Please select a ${field.label.toLowerCase()}`}
    >
      <ResponsiveCommandSelect
        value={selectCurrentValue || ""}
        onValueChange={(value) => setValue(fieldName, value, { shouldValidate: true })}
        options={
          field.options?.map((option) => ({
            value: option.value,
            label: option.label,
          })) || []
        }
        placeholder={field.placeholder || `Select ${field.label.toLowerCase()}`}
        title={field.label}
        searchPlaceholder={`Search ${field.label.toLowerCase()}...`}
        highZIndex={true}
        expandInline={true}
        expandInlineVariant={ctx.expandInlineVariant ?? "replace"}
      />
    </FieldWrapper>
  );
};

/**
 * Render a client-select field.
 */
export const ClientSelectFieldRenderer: React.FC<{ ctx: FieldRenderContext }> = ({ ctx }) => {
  const {
    fieldName,
    field,
    setValue,
    watchedValues,
    errors,
    clientOptions,
    clientsLoading,
    isSearching,
    searchValue,
    setSearchValue,
    isClientUser,
    clientSelfContactId,
    clientSelfContactName,
  } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string;
  const currentValue = watchedValues[fieldName] || "";

  // A client_user filing this form is almost always acting on their own behalf.
  // Offer "assign to me" two ways: (1) auto-fill their own contact once, when the
  // field is still empty, so a single-contact client never has to touch the
  // picker; (2) a small "Assign to me" button so a company-wide client who has
  // navigated away can snap it back. Auto-fill runs once (ref-guarded) so it
  // never fights a user who deliberately picks a different contact.
  const showAssignToMe = Boolean(isClientUser && clientSelfContactId);
  const autoFilledRef = useRef(false);

  useEffect(() => {
    if (!showAssignToMe || !clientSelfContactId) return;
    if (autoFilledRef.current) return;
    if (currentValue) {
      // Already has a value (edit / prefill) — don't override, but treat the
      // one-shot auto-fill as spent so we don't clobber a later manual clear.
      autoFilledRef.current = true;
      return;
    }
    autoFilledRef.current = true;
    setValue(fieldName, clientSelfContactId, { shouldValidate: true });
  }, [showAssignToMe, clientSelfContactId, currentValue, fieldName, setValue]);

  const assignToMe = useCallback(() => {
    if (clientSelfContactId) {
      setValue(fieldName, clientSelfContactId, { shouldValidate: true });
    }
  }, [clientSelfContactId, fieldName, setValue]);

  return (
    <FieldWrapper
      fieldName={fieldName}
      field={field}
      hasError={hasError}
      errorMessage={errorMessage || "Please select a contact"}
    >
      <div className={cn("w-full space-y-2", hasError && "animate-shake")}>
        {showAssignToMe ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8"
            onClick={assignToMe}
            disabled={currentValue === clientSelfContactId}
          >
            <UserCheck className="h-4 w-4 mr-2" />
            {currentValue === clientSelfContactId
              ? `You${clientSelfContactName ? ` (${clientSelfContactName})` : ""}`
              : "Assign to me"}
          </Button>
        ) : null}
        <ClientSelectWithModal
          value={currentValue}
          onValueChange={(val: string) => setValue(fieldName, val, { shouldValidate: true })}
          items={clientOptions || []}
          placeholder={field.placeholder || "Search for contact..."}
          disabled={clientsLoading}
          error={hasError}
          isSearching={isSearching}
          searchTerm={searchValue}
          onSearchChange={setSearchValue}
          highZIndex={true}
          expandInline={true}
          expandInlineVariant={ctx.expandInlineVariant ?? "replace"}
        />
      </div>
    </FieldWrapper>
  );
};

/**
 * Render a ticket-select field.
 */
export const TicketSelectFieldRenderer: React.FC<{ ctx: FieldRenderContext }> = ({ ctx }) => {
  const { fieldName, field, setValue, watchedValues, errors } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string;
  const ticketValue = watchedValues[fieldName];

  return (
    <FieldWrapper
      fieldName={fieldName}
      field={field}
      hasError={hasError}
      errorMessage={errorMessage || "Please select a ticket"}
    >
      <TicketSelectWithModal
        value={ticketValue || ""}
        onValueChange={(val: string) => setValue(fieldName, val, { shouldValidate: true })}
        placeholder={field.placeholder || "Search for ticket..."}
        fullWidth
        highZIndex
      />
    </FieldWrapper>
  );
};

/**
 * Render a user field.
 */
export const UserFieldRenderer: React.FC<{ ctx: FieldRenderContext }> = ({ ctx }) => {
  const { fieldName, field, setValue, watchedValues, errors } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string;
  const userSelectValue = watchedValues[fieldName];

  const handleUserSelect = useCallback(
    async (userId: string) => {
      setValue(fieldName, userId, { shouldValidate: true });
    },
    [setValue, fieldName]
  );

  return (
    <FieldWrapper
      fieldName={fieldName}
      field={field}
      hasError={hasError}
      errorMessage={errorMessage || "Please select a user"}
    >
      <UserSelect
        currentUserId={userSelectValue || ""}
        onUserSelect={handleUserSelect}
        placeholder={field.placeholder || "Select user..."}
        showUnassigned={true}
        expandInline={true}
        expandInlineVariant={ctx.expandInlineVariant ?? "replace"}
      />
    </FieldWrapper>
  );
};

/**
 * Render a company-select field.
 */
export const CompanySelectFieldRenderer: React.FC<{ ctx: FieldRenderContext }> = ({ ctx }) => {
  const { fieldName, field, setValue, watchedValues, errors } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string;
  const companyValue = watchedValues[fieldName];

  return (
    <FieldWrapper
      fieldName={fieldName}
      field={field}
      hasError={hasError}
      errorMessage={errorMessage || "Please select a company"}
    >
      <CompanySelect
        value={companyValue || ""}
        onValueChange={(val: string) => setValue(fieldName, val, { shouldValidate: true })}
        placeholder={field.placeholder || "Search for company..."}
        expandInline={true}
        expandInlineVariant={ctx.expandInlineVariant ?? "replace"}
      />
    </FieldWrapper>
  );
};

/**
 * Render a multi-user field.
 */
export const MultiUserFieldRenderer: React.FC<{ ctx: FieldRenderContext }> = ({ ctx }) => {
  const { fieldName, field, setValue, watchedValues, errors, appUserId } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string;
  const config = field.multiUserConfig || {};
  const currentUserIds = watchedValues[fieldName];

  // Ensure we always have an array, handle various input types
  let safeUserIds: string[] = [];
  if (Array.isArray(currentUserIds)) {
    safeUserIds = currentUserIds.filter((id) => id && typeof id === "string");
  } else if (typeof currentUserIds === "string" && currentUserIds.trim()) {
    safeUserIds = [currentUserIds.trim()];
  } else if (currentUserIds) {
    safeUserIds = [String(currentUserIds)];
  }

  const handleUsersSelect = useCallback(
    async (userIds: string[]) => {
      // Handle special cases
      const processedUserIds = userIds
        .map((userId: string) => {
          if (userId === "me") {
            return appUserId || "";
          } else if (userId === "unassigned") {
            return "";
          }
          return userId;
        })
        .filter((userId: string) => userId && userId.trim());

      setValue(fieldName, processedUserIds, { shouldValidate: true });
    },
    [appUserId, setValue, fieldName]
  );

  return (
    <FieldWrapper
      fieldName={fieldName}
      field={field}
      hasError={hasError}
      errorMessage={errorMessage || "Please assign at least one user"}
    >
      <MultiUserSelect
        currentUserIds={safeUserIds}
        onUsersSelect={handleUsersSelect}
        placeholder={field.placeholder || "Assign to users..."}
        showUnassigned={config.showUnassigned ?? true}
        showAllUsers={config.showAllUsers ?? false}
        maxUsers={config.maxUsers ?? 5}
        expandInline={true}
        expandInlineVariant={ctx.expandInlineVariant ?? "replace"}
      />
    </FieldWrapper>
  );
};
